import { useState, type SyntheticEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiRequestError } from '../lib/api';
import { Button, Card, ErrorNote, Field, Label, inputClass } from '../components/ui';
import type { UserProfile } from '../../shared/types';

const EXAMPLES = [
  'Get my daughters tickets to the next tour.',
  'Buy my oldest daughter a car.',
  'Generate business instead of waiting for it to arrive.',
  'Be comfortable asking for money and making offers.',
];

export function Onboarding({
  profile,
  onComplete,
}: {
  profile: UserProfile | null;
  onComplete: () => Promise<UserProfile>;
}) {
  const navigate = useNavigate();
  const [why, setWhy] = useState('');
  const [comfort, setComfort] = useState(4);
  const [avoided, setAvoided] = useState('');
  const [target, setTarget] = useState(profile?.dailyCallTarget ?? 5);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: SyntheticEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.onboard({
        why,
        comfortBaseline: comfort,
        avoidedCallTypes: avoided || undefined,
        dailyCallTarget: target,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      await onComplete();
      void navigate('/today', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError ? caught.message : 'Could not save that. Try again.',
      );
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex w-full max-w-xl flex-col gap-5 px-4 pt-8 pb-16">
      <div>
        <h1 className="text-2xl font-semibold text-mist-200">
          What becomes possible if you&rsquo;re comfortable picking up the phone?
        </h1>
        <p className="mt-2 text-sm text-mist-400">
          Write it the way you&rsquo;d say it. One line per thing. This is what the coach connects
          the boring reps back to.
        </p>
      </div>

      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-5">
        <Card>
          <Field label="Your why">
            <textarea
              className={`${inputClass} min-h-[9rem] resize-y`}
              value={why}
              onChange={(event) => {
                setWhy(event.target.value);
              }}
              placeholder={EXAMPLES.join('\n')}
              required
            />
          </Field>
        </Card>

        <Card className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <Label>How comfortable are you on the phone right now?</Label>
            <input
              type="range"
              min={1}
              max={10}
              value={comfort}
              onChange={(event) => {
                setComfort(Number(event.target.value));
              }}
              aria-label="Current phone comfort, 1 to 10"
            />
            <div className="flex justify-between text-xs text-mist-400">
              <span>1 — I put it off</span>
              <span className="text-lg font-semibold text-mist-200">{comfort}</span>
              <span>10 — no hesitation</span>
            </div>
          </div>

          <Field
            label="What kinds of call do you avoid?"
            hint="Optional. It tells the engine which dimension to go easy on first."
          >
            <input
              className={inputClass}
              value={avoided}
              onChange={(event) => {
                setAvoided(event.target.value);
              }}
              placeholder="Asking for something, anyone senior, cold prospects…"
            />
          </Field>

          <Field label="Calls per day" hint="Five is the default. You can change it any time.">
            <div className="flex gap-2">
              {[3, 5, 7, 10].map((option) => (
                <Button
                  key={option}
                  type="button"
                  variant={target === option ? 'primary' : 'secondary'}
                  className="flex-1"
                  onClick={() => {
                    setTarget(option);
                  }}
                >
                  {option}
                </Button>
              ))}
            </div>
          </Field>
        </Card>

        {error ? <ErrorNote>{error}</ErrorNote> : null}

        <Button type="submit" variant="primary" full disabled={busy || why.trim().length === 0}>
          {busy ? 'Building day one…' : 'Start'}
        </Button>
      </form>
    </main>
  );
}
